import React from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Check, ChevronRight, Quote } from "lucide-react";
import { SITE } from "@/lib/site";
import { getModule, getCategory } from "@/lib/modules";
import { Container, Section, SectionHeader, Reveal, CTAButton, Kicker, useSeo } from "@/components/ui-kit";
import { ProductFrame } from "@/components/mockups/ProductFrame";
import { ModuleCard } from "@/components/sections/ModuleExplorer";
import { FAQSection, CTABand } from "@/components/sections/Social";
import RAMSDemo from "@/components/demos/RAMSDemo";
import EmailDemo from "@/components/demos/EmailDemo";

const DEMOS = {
  rams: { component: RAMSDemo, eyebrow: "Try it · live simulation", title: "Type a job. Watch the RAMS draft itself.", desc: "Describe any maintenance job in plain English — trade, where it is, who's around — and see the hazards, controls, risk scores, method steps and PPE appear the way they would in Fixlr." },
  email: { component: EmailDemo, eyebrow: "Try it · live simulation", title: "Paste an email. Get a job card.", desc: "Drop in a tenant complaint, a client request or a supplier's completion email and watch Fixlr pick out the sender, property, trade, urgency, asset and access window — then convert it." },
};

const ModulePage = () => {
  const { slug } = useParams();
  const m = getModule(slug);
  useSeo({ title: m ? `${m.name} — ${m.headline}` : "Product", description: m?.summary, path: m ? `/product/${m.slug}` : "/product", image: m ? `/og/${m.slug}.png` : undefined });
  if (!m) return <Navigate to="/product" replace />;
  const cat = getCategory(m.category);
  const related = (m.related || []).map(getModule).filter(Boolean);
  const liveUrl = m.liveUrl?.replace("https://", "");
  const demo = m.demo ? DEMOS[m.demo] : null;

  return (
    <div data-testid={`module-page-${m.slug}`}>
      <section className="relative overflow-hidden">
        <div className="absolute inset-0 bg-grid-dark mask-fade-b opacity-60" aria-hidden />
        <div className="orb h-[460px] w-[460px] -top-40 right-[-100px] bg-mk-lime/15" aria-hidden />
        <Container className="relative pt-12 pb-16 lg:pt-16 lg:pb-24">
          <nav className="flex items-center gap-1.5 text-xs text-mk-muted" aria-label="Breadcrumb" data-testid="module-breadcrumb">
            <Link to="/product" className="hover:text-mk-text">Product</Link><ChevronRight className="h-3 w-3" />
            <Link to={`/platform/${cat.slug}`} className="hover:text-mk-text">{cat.name}</Link><ChevronRight className="h-3 w-3" />
            <span className="text-mk-dim">{m.name}</span>
          </nav>
          <div className="mt-8 grid lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-5 min-w-0">
              <Kicker><m.icon className="h-3.5 w-3.5" />{m.kicker} · {cat.name}</Kicker>
              <h1 className="mt-6 font-display text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight text-mk-text leading-[1.02] text-balance">{m.headline}</h1>
              <p className="mt-6 text-base md:text-lg text-mk-dim leading-relaxed">{m.summary}</p>
              <ul className="mt-6 space-y-2.5">
                {m.bullets.map((b) => <li key={b} className="flex items-start gap-2.5 text-sm text-mk-text/90"><Check className="h-4 w-4 text-mk-lime shrink-0 mt-0.5" />{b}</li>)}
              </ul>
              <div className="mt-8 flex flex-wrap gap-3">
                <CTAButton href={SITE.signupUrl} testId="module-hero-trial-btn">Start free trial <ArrowRight className="h-4 w-4" /></CTAButton>
                {m.crossLink && <Link to={m.crossLink.to} className="btn-ghost" data-testid="module-hero-crosslink">{m.crossLink.label} <ArrowRight className="h-4 w-4" /></Link>}
                {demo ? <a href="#demo" onClick={(e) => { e.preventDefault(); document.getElementById("demo")?.scrollIntoView({ behavior: "smooth", block: "start" }); }} className="btn-ghost" data-testid="module-hero-demo-link">Try the live demo <ChevronRight className="h-4 w-4" /></a> : <CTAButton to="/contact" variant="ghost" testId="module-hero-demo-btn">Book a demo</CTAButton>}
              </div>
            </div>
            <Reveal className="lg:col-span-7 min-w-0">
              <ProductFrame shot={m.shot} mockup={m.mockup} url={liveUrl} alt={`${m.name} in Fixlr`} glow testId="module-hero-frame" />
              {m.stats && (
                <div className="mt-6 grid grid-cols-3 gap-3">
                  {m.stats.map((s) => <div key={s.label} className="rounded-xl border border-mk-border bg-mk-surface p-4"><div className="font-display text-2xl font-bold text-mk-lime">{s.value}</div><div className="text-xs text-mk-dim mt-0.5">{s.label}</div></div>)}
                </div>
              )}
            </Reveal>
          </div>
        </Container>
      </section>

      {demo && (
        <Section id="demo" className="scroll-mt-20 bg-mk-surface/40 border-y border-mk-border" data-testid={`module-demo-${m.demo}`}>
          <Container>
            <SectionHeader eyebrow={demo.eyebrow} title={demo.title} desc={demo.desc} />
            <div className="mt-10"><demo.component /></div>
          </Container>
        </Section>
      )}

      {m.pains && (
        <Section className={demo ? "" : "bg-mk-surface/40 border-y border-mk-border"}>
          <Container>
            <SectionHeader eyebrow="The problem" title={`What ${m.name.toLowerCase()} looks like without Fixlr.`} />
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {m.pains.map((p, i) => <Reveal key={p.title} delay={i * 0.06} className="rounded-2xl border border-mk-border bg-mk-bg p-6"><div className="font-mono text-xs text-red-300">0{i + 1}</div><h3 className="mt-3 font-display text-lg font-semibold text-mk-text">{p.title}</h3><p className="mt-2 text-sm text-mk-dim leading-relaxed">{p.desc}</p></Reveal>)}
            </div>
          </Container>
        </Section>
      )}

      {m.deepDives?.map((d, i) => (
        <Section key={d.title} className={i % 2 ? "bg-mk-surface/40 border-y border-mk-border" : ""}>
          <Container>
            <div className="grid lg:grid-cols-12 gap-10 items-center">
              <div className={`lg:col-span-5 min-w-0 ${i % 2 ? "lg:order-2" : ""}`}>
                <SectionHeader eyebrow={d.kicker} title={d.title} desc={d.body} />
              </div>
              <Reveal className={`lg:col-span-7 min-w-0 ${i % 2 ? "lg:order-1" : ""}`}><ProductFrame shot={d.shot ? `/shots/${d.shot}.png` : null} mockup={d.mockup} url={liveUrl} alt={d.title} testId={`module-deepdive-frame-${i}`} /></Reveal>
            </div>
          </Container>
        </Section>
      ))}

      {m.steps && (
        <Section className="bg-mk-surface/40 border-y border-mk-border">
          <Container>
            <SectionHeader eyebrow="How it works" title={`${m.name}, step by step.`} />
            <div className="mt-10 grid md:grid-cols-4 gap-4">
              {m.steps.map((s, i) => <Reveal key={s.title} delay={i * 0.07} className="relative rounded-2xl border border-mk-border bg-mk-bg p-6"><div className="font-mono text-sm text-mk-lime">STEP 0{i + 1}</div><h3 className="mt-3 font-display text-lg font-semibold text-mk-text">{s.title}</h3><p className="mt-2 text-sm text-mk-dim leading-relaxed">{s.desc}</p></Reveal>)}
            </div>
          </Container>
        </Section>
      )}

      {m.quote && (
        <Section>
          <Container>
            <Reveal className="relative rounded-3xl border border-mk-lime/30 bg-mk-lime/[0.04] p-8 sm:p-12 text-center">
              <Quote className="mx-auto h-8 w-8 text-mk-lime/50" />
              <p className="mt-4 font-display text-2xl sm:text-3xl font-medium text-mk-text text-balance max-w-3xl mx-auto">“{m.quote.text}”</p>
              <div className="mt-5 text-sm text-mk-dim"><span className="text-mk-text font-semibold">{m.quote.name}</span> · {m.quote.role}</div>
            </Reveal>
          </Container>
        </Section>
      )}

      {m.faqs && <FAQSection items={m.faqs} title={`${m.name} — common questions.`} eyebrow="FAQ" />}

      {related.length > 0 && (
        <Section className="bg-mk-surface/40 border-y border-mk-border">
          <Container>
            <SectionHeader eyebrow="Related modules" title="Works best with." />
            <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-4">{related.map((r) => <ModuleCard key={r.slug} m={r} testIdPrefix="related-module" />)}</div>
          </Container>
        </Section>
      )}

      <CTABand title={`Ready to ship ${m.name.toLowerCase()}?`} />
    </div>
  );
};

export default ModulePage;
